import { useBrand } from "hooks/useBrand";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { PublicPlanCardProps } from "./pricing.types";

import styles from "./PublicPlanCard.module.css";

/** A single plan column: name, price, what the price includes, features, and a sign-up link. */
const PublicPlanCard = ({ plan, pricing }: PublicPlanCardProps) => {
	const { t } = useTranslation("pricingPage");
	const brand = useBrand();

	return (
		<section className={`${styles.card} ${plan.isHighlighted ? styles.cardHighlighted : ""}`}>
			{plan.isHighlighted && <span className={styles.badge}>{t("recommended")}</span>}

			<h2 className={styles.planName}>{t(`${plan.key}.name`)}</h2>
			<p className={styles.planDescription}>{t(`${plan.key}.description`)}</p>

			<div className={styles.price}>
				<span className={styles.priceAmount}>{plan.price}</span>
				{plan.showPeriod && <span className={styles.pricePeriod}>{t("perMonth")}</span>}
			</div>

			{plan.priceNoteKeys.map((noteKey: string) => (
				<p key={noteKey} className={styles.priceNote}>
					{t(`${plan.key}.${noteKey}`, {
						base: pricing.base,
						currency: pricing.currencyLabel,
						tax: pricing.tax,
						taxRate: pricing.taxRate
					})}
				</p>
			))}

			<ul className={styles.featureList}>
				{plan.featureKeys.map((featureKey: string) => (
					<li key={featureKey} className={styles.featureItem}>
						<span aria-hidden="true" className={styles.featureMark}>
							✦
						</span>
						<span>{t(`${plan.key}.features.${featureKey}`, brand)}</span>
					</li>
				))}
			</ul>

			<Link className={styles.cta} to={plan.ctaTo}>
				{t(`${plan.key}.cta`)}
			</Link>
		</section>
	);
};

export default PublicPlanCard;
